/**
 * Parser for the training-slide body markup: a deliberately small subset so
 * HR-authored content gets real structure (the reference toolbox talks are
 * mostly numbered lists) without a rich-text editor or raw HTML. The output
 * is a block list the renderer turns into text nodes only — inline bold and
 * links are handled per-line by LinkifiedText, so the XSS posture is
 * unchanged.
 *
 * Line rules (leading whitespace ignored):
 *   ## text     subheading
 *   - text      bullet item (consecutive items form one list; * also works)
 *   1. text     numbered item (any number; `1)` also works)
 *   > text      callout box (consecutive > lines form one callout)
 *   blank line  paragraph break
 *   anything    paragraph text (consecutive lines stay in one paragraph)
 */

export type SlideBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'bullets'; items: string[] }
  | { type: 'numbered'; items: string[] }
  | { type: 'callout'; text: string };

const HEADING_RE = /^##\s+(.*)$/;
const BULLET_RE = /^[-*]\s+(.*)$/;
const NUMBERED_RE = /^\d+[.)]\s+(.*)$/;
const CALLOUT_RE = /^>\s?(.*)$/;

export function parseSlideBody(body: string): SlideBlock[] {
  const blocks: SlideBlock[] = [];
  // Open accumulators; only one is non-empty at a time.
  let paragraph: string[] = [];
  let bullets: string[] = [];
  let numbered: string[] = [];
  let callout: string[] = [];

  function flush() {
    if (paragraph.length > 0) blocks.push({ type: 'paragraph', text: paragraph.join('\n') });
    if (bullets.length > 0) blocks.push({ type: 'bullets', items: bullets });
    if (numbered.length > 0) blocks.push({ type: 'numbered', items: numbered });
    if (callout.length > 0) blocks.push({ type: 'callout', text: callout.join('\n') });
    paragraph = [];
    bullets = [];
    numbered = [];
    callout = [];
  }

  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (line === '') {
      flush();
      continue;
    }
    const heading = HEADING_RE.exec(line);
    if (heading) {
      flush();
      blocks.push({ type: 'heading', text: heading[1]!.trim() });
      continue;
    }
    const bullet = BULLET_RE.exec(line);
    if (bullet) {
      if (bullets.length === 0) flush();
      bullets.push(bullet[1]!);
      continue;
    }
    const item = NUMBERED_RE.exec(line);
    if (item) {
      if (numbered.length === 0) flush();
      numbered.push(item[1]!);
      continue;
    }
    const note = CALLOUT_RE.exec(line);
    if (note) {
      if (callout.length === 0) flush();
      callout.push(note[1]!);
      continue;
    }
    if (paragraph.length === 0) flush();
    paragraph.push(line);
  }
  flush();
  return blocks;
}
